import Modal from '@components/atoms/modals/modal.tsx';
import { UppyDashboard } from '@components/shared/upload/uppy-dashboard.tsx';
import { MEDIA_QUERIES, useMediaQuery } from '@utils/hooks/use-media-query.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useTranslation } from 'react-i18next';
import { DocumentTypeOptions } from '@utils/types/editors/utils.ts';
import { UploadAlignmentPanel } from './upload-alignment-panel';
import { UploadSettingsPanel } from './upload-settings-panel';
import { useUpload } from './use-upload';

type Props = {
  type: DocumentTypeOptions | 'align' | '';
  setType: (type: DocumentTypeOptions | 'align' | '') => void;
  initialFiles?: File[];
  initialFolderId?: number;
};

const Upload = ({ type, setType, initialFiles, initialFolderId }: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const [isMobile] = useMediaQuery(
    `(max-width: ${MEDIA_QUERIES.LARGE_TABLET})`
  );

  const {
    state,
    dispatch,
    activeType,
    setActiveType,
    diarization,
    setDiarization,
    isSubmitting,
    useDirectAzureUpload,
    setUseDirectAzureUpload,
    hasLargeFiles,
    selectedFolderId,
    setSelectedFolderId,
    speakerValidationError,
    setSpeakerValidationError,
    detection,
    detectedMatch,
    userOverrodeLanguageRef,
    languageOptions,
    folderOptions,
    models,
    hasUrlFiles,
    hasActiveTusUpload,
    primaryUploadDisabled,
    allowEscClose,
    shouldConfirmBeforeClose,
    getConfirmMessage,
    validateAlignmentMediaFile,
    validateAlignmentTranscriptFile,
    handleUploadClick,
    getNonAlignButtonLabel,
    onClose
  } = useUpload({ type, setType, initialFiles, initialFolderId });

  return (
    <Modal
      isOpen={type !== ''}
      onClose={onClose}
      closeOnOutsideClick={false}
      closeOnEsc={allowEscClose}
      confirmBeforeClose={shouldConfirmBeforeClose}
      confirmMessage={getConfirmMessage()}
      size={isMobile ? 'large' : 'medium'}
      customWidth={isMobile ? undefined : '960px'}
      customMaxHeight="85vh"
      title={
        type === 'align'
          ? 'Force Alignment'
          : activeType === 'document'
            ? t('transcript')
            : t('subtitles')
      }
      caption={t('upload-file')}
      headerExtra={
        type !== 'align' ? (
          <div
            className="flex gap-1 p-1 rounded-2xl"
            style={{ backgroundColor: getColour('baseLayer') }}
          >
            {(['document', 'caption'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveType(tab)}
                className="py-1.5 px-4 rounded-2xl text-sm font-semibold transition-all duration-150"
                style={{
                  backgroundColor:
                    activeType === tab
                      ? getColour('secondLayer')
                      : 'transparent',
                  color:
                    activeType === tab
                      ? getColour('normalText')
                      : getColour('inactiveText'),
                  boxShadow:
                    activeType === tab ? '0 1px 4px rgba(0,0,0,0.18)' : 'none'
                }}
              >
                {tab === 'document' ? t('transcript') : t('subtitles')}
              </button>
            ))}
          </div>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-3">
        {/* File area — full width */}
        {type === 'align' ? (
          <UploadAlignmentPanel
            alignmentMediaFile={state.alignmentMediaFile}
            alignmentTranscriptFile={state.alignmentTranscriptFile}
            uploading={state.uploading}
            alignmentProgress={state.alignmentProgress}
            onSetMediaFile={file =>
              dispatch({ type: 'SET_ALIGNMENT_MEDIA_FILE', payload: file })
            }
            onSetTranscriptFile={file =>
              dispatch({ type: 'SET_ALIGNMENT_TRANSCRIPT_FILE', payload: file })
            }
            validateMedia={validateAlignmentMediaFile}
            validateTranscript={validateAlignmentTranscriptFile}
          />
        ) : (
          <div
            className="rounded-2xl overflow-hidden"
            style={{
              backgroundColor: getColour('secondLayer'),
              border: `1px solid ${getColour('border')}`
            }}
            onDragOver={e => {
              if (Array.from(e.dataTransfer.types).includes('Files')) {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
              }
            }}
            onDrop={e => {
              if (!Array.from(e.dataTransfer.types).includes('Files')) return;
              e.preventDefault();
              const uppy = state.uppy;
              if (!uppy) return;
              Array.from(e.dataTransfer.files).forEach(file => {
                try {
                  uppy.addFile({
                    name: file.name,
                    type: file.type,
                    data: file,
                    source: 'Local',
                    isRemote: false
                  });
                } catch {
                  /* ignore duplicate / restricted file errors */
                }
              });
            }}
          >
            {state.uppy && (
              <UppyDashboard
                uppy={state.uppy}
                height={300}
                width={'100%'}
                plugins={[
                  'Webcam',
                  'Audio',
                  'ScreenCapture',
                  'OneDrive',
                  'UrlPlugin'
                ]}
                onRecordingStateChange={isRecording =>
                  dispatch({ type: 'SET_IS_RECORDING', payload: isRecording })
                }
              />
            )}
          </div>
        )}

        {/* Settings row — horizontal below file area */}
        <UploadSettingsPanel
          type={type}
          activeType={activeType}
          selectedLanguage={state.selectedLanguage}
          languageOptions={languageOptions}
          detection={detection}
          detectedMatch={detectedMatch}
          userOverrodeLanguageRef={userOverrodeLanguageRef}
          dispatch={dispatch}
          models={models}
          diarization={diarization}
          setDiarization={setDiarization}
          speakerValidationError={speakerValidationError}
          setSpeakerValidationError={setSpeakerValidationError}
          minSpeakers={state.minSpeakers}
          maxSpeakers={state.maxSpeakers}
          useDirectAzureUpload={useDirectAzureUpload}
          setUseDirectAzureUpload={setUseDirectAzureUpload}
          hasLargeFiles={hasLargeFiles}
          hasUrlFiles={hasUrlFiles}
          selectedFolderId={selectedFolderId}
          setSelectedFolderId={setSelectedFolderId}
          folderOptions={folderOptions}
          isRecording={state.isRecording}
          uploading={state.uploading}
          azureBlobProgress={state.azureBlobProgress}
          isSubmitting={isSubmitting}
          hasActiveTusUpload={hasActiveTusUpload}
          primaryUploadDisabled={primaryUploadDisabled}
          alignmentMediaFile={state.alignmentMediaFile}
          alignmentTranscriptFile={state.alignmentTranscriptFile}
          onUpload={handleUploadClick}
          getNonAlignButtonLabel={getNonAlignButtonLabel}
        />
      </div>
    </Modal>
  );
};

export default Upload;
